import type { Container, Graphics, Sprite } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import type { EnemyState } from '../../sim/state';
import { BURST_AT } from '../boss-moments';
import type { Frame } from '../frame';
import type { Shape } from '../paint';
import { add, hide, keepIn, setTint } from '../util';
import { partyColors } from '../vent-mane';
import { BELLY, FINE, SIZE, WING_FRAMES } from './moustiques-looks';
import {
  FLOAT,
  ORBITERS,
  POP,
  type Spark,
  TRAIL,
  alive,
  jitter,
  orbit,
  pop,
  target,
  trailSpot,
  wingFrame,
} from './moustiques-moments';

export interface Swarm {
  readonly orbiters: readonly Sprite[];
  readonly trail: readonly Sprite[];
  readonly diedAt: number[];
  readonly lastX: number[];
  readonly lastY: number[];
  readonly colors: number[];
}

export interface Farewell {
  readonly swarm: Swarm;
  readonly start: number;
  readonly x: number;
  readonly y: number;
  readonly unit: number;
  readonly share: number;
}

export function wingsAt(shapes: readonly Shape[], index: number, beats: number, calm: boolean) {
  const shape = shapes[wingFrame(index, beats, calm, WING_FRAMES)] ?? shapes[0];
  if (shape === undefined) {
    throw new Error('the mosquitoes have no wings painted');
  }
  return shape.texture;
}

export function drawSpark(
  fx: Graphics,
  x: number,
  y: number,
  unit: number,
  color: number,
  burst: Spark,
): void {
  fx.circle(x, y, burst.glow * unit).fill({ color, alpha: burst.alpha * 0.8 });
  fx.circle(x, y, burst.ring * unit).stroke({ width: 1.2, color, alpha: burst.alpha });
}

export function drawFarewell(
  exit: Farewell,
  frame: Frame,
  fx: Graphics,
  layers: { readonly back: Container; readonly front: Container },
  wings: readonly Shape[],
): boolean {
  const { swarm, x, y, unit, share } = exit;
  const { palette, calm } = frame;
  const sinceBurst = (frame.show - exit.start) / frame.tempo.ticksPerBeat - BURST_AT;
  if (sinceBurst > POP.beats + 1) {
    return false;
  }
  const beats = frame.show / frame.tempo.ticksPerBeat;
  const party = partyColors(palette);
  hide(...swarm.trail);
  swarm.orbiters.forEach((sprite, index) => {
    if (!alive(index, share)) {
      sprite.visible = false;
      return;
    }
    const spot = orbit(index, beats);
    const px = x + spot.x * unit;
    const py = y + spot.y * unit;
    const burst = sinceBurst >= 0 ? pop(index, sinceBurst) : undefined;
    if (burst === 'gone' || (burst !== undefined && !calm)) {
      sprite.visible = false;
      if (burst !== 'gone') {
        drawSpark(fx, px, py, unit, party[index % party.length] ?? palette.or, burst);
      }
      return;
    }
    keepIn(spot.front ? layers.front : layers.back, sprite);
    sprite.visible = true;
    sprite.texture = wingsAt(wings, index, beats, calm);
    setTint(sprite, palette.badVibeRim);
    sprite.position.set(px, py);
    sprite.scale.set((SIZE * unit) / FINE);
    sprite.alpha = burst?.alpha ?? 1;
  });
  return true;
}

export function createSwarmPool(back: Container, front: Container, wings: readonly Shape[]) {
  const spare: Swarm[] = [];
  const first = wings[0];
  if (first === undefined) {
    throw new Error('the mosquitoes have no wings painted');
  }
  return {
    take(): Swarm {
      return (
        spare.pop() ?? {
          orbiters: Array.from({ length: ORBITERS }, () => add(back, first)),
          trail: Array.from({ length: TRAIL }, () => add(front, first)),
          diedAt: Array.from({ length: ORBITERS }, () => Number.NaN),
          lastX: Array.from({ length: ORBITERS }, () => 0),
          lastY: Array.from({ length: ORBITERS }, () => 0),
          colors: Array.from({ length: ORBITERS }, () => 0xffffff),
        }
      );
    },
    release(swarm: Swarm): void {
      hide(...swarm.orbiters, ...swarm.trail);
      swarm.diedAt.fill(Number.NaN);
      spare.push(swarm);
    },
  };
}

type Sting = NonNullable<EnemyState['sting']>;

export function drawTarget(ground: Graphics, sting: Sting, reach: number, frame: Frame): void {
  const { ticksPerBeat } = frame.tempo;
  const shown = target(
    (frame.now - sting.startTick) / ticksPerBeat,
    (sting.landTick - sting.startTick) / ticksPerBeat,
  );
  if (shown === undefined) {
    return;
  }
  const color = frame.palette.rouge;
  const radius = reach * shown.radius;
  ground.circle(sting.x, sting.y, radius).fill({ color, alpha: frame.calm ? 0.16 : shown.fill });
  if (shown.ring === undefined) {
    const dashes = 16;
    for (let index = 0; index < dashes; index += 1) {
      const from = (index / dashes) * Math.PI * 2;
      const to = from + (Math.PI * 2) / dashes / 1.8;
      ground.moveTo(sting.x + Math.cos(from) * radius, sting.y + Math.sin(from) * radius);
      ground.arc(sting.x, sting.y, radius, from, to);
    }
    ground.stroke({ width: 2.5, color, alpha: 0.9 });
    return;
  }
  ground
    .circle(sting.x, sting.y, reach * shown.ring.radius)
    .stroke({ width: 3, color, alpha: shown.ring.alpha });
}

export function drawTrail(
  fx: Graphics,
  swarm: Swarm,
  sting: Sting,
  tip: { x: number; y: number },
  unit: number,
  shown: number,
  frame: Frame,
  wings: readonly Shape[],
): void {
  const beats = frame.now / frame.tempo.ticksPerBeat;
  const p = (frame.now - sting.startTick) / (sting.endTick - sting.startTick);
  const gorged = sting.gorged === true && frame.now >= sting.landTick;
  swarm.trail.forEach((sprite, index) => {
    const { out, arc, sway } = trailSpot(index, p);
    sprite.visible = out > 0.02;
    sprite.texture = wingsAt(wings, index, beats, frame.calm);
    setTint(sprite, frame.palette.badVibeRim);
    const float = jitter(index, beats, frame.calm ? FLOAT.calm : FLOAT.live);
    const x = lerp(tip.x, sting.x, out) + (sway + float.x) * unit;
    const y = lerp(tip.y, sting.y, out) - arc + float.y * unit;
    sprite.position.set(x, y);
    sprite.scale.set((SIZE * unit) / FINE);
    sprite.alpha = shown;
    if (gorged && sprite.visible) {
      fx.circle(x, y + BELLY.y * SIZE * unit, BELLY.gorged * SIZE * unit)
        .fill({ color: frame.palette.rouge, alpha: shown })
        .stroke({ width: 1, color: 0xffffff, alpha: shown });
    }
  });
}
